import { z } from 'zod';
import { attachmentViewSchema } from './attachments.js';
import { idSchema } from './common.js';
import { stateCategorySchema } from './projects.js';

export const WORK_ITEM_TYPES = ['bug', 'task'] as const;
export type WorkItemType = (typeof WORK_ITEM_TYPES)[number];

export const PRIORITIES = ['none', 'low', 'medium', 'high', 'urgent'] as const;
export type Priority = (typeof PRIORITIES)[number];

/** Rango persistido para ordenar sin depender del orden de declaración de un tipo (diseño §3.3). */
export const PRIORITY_RANK: Record<Priority, number> = {
  none: 0,
  low: 1,
  medium: 2,
  high: 3,
  urgent: 4,
};

export const RELATION_KINDS = ['blocks', 'duplicates', 'relates'] as const;
export type RelationKind = (typeof RELATION_KINDS)[number];

/** `outgoing`: este item es el origen de la relación. `incoming`: es el destino. */
export const RELATION_DIRECTIONS = ['outgoing', 'incoming'] as const;
export type RelationDirection = (typeof RELATION_DIRECTIONS)[number];

export const WATCHER_SOURCES = ['manual', 'created', 'assigned', 'commented', 'mentioned'] as const;
export type WatcherSource = (typeof WATCHER_SOURCES)[number];

export const SORTS = ['updated', 'created', 'priority', 'due', 'manual'] as const;
export type Sort = (typeof SORTS)[number];

export const ORDERS = ['asc', 'desc'] as const;
export type Order = (typeof ORDERS)[number];

/** `VIT-123` o UUID. El parser normaliza a mayúsculas antes de resolver (diseño §3.3). */
export const REF_PATTERN = /^[A-Za-z][A-Za-z0-9]{1,7}-\d+$/;

export const parseRef = (ref: string): { key: string; number: number } | null => {
  if (!REF_PATTERN.test(ref)) return null;
  const separator = ref.lastIndexOf('-');
  return { key: ref.slice(0, separator).toUpperCase(), number: Number(ref.slice(separator + 1)) };
};

export const workItemSchema = z.object({
  id: idSchema,
  ref: z.string(),
  projectId: idSchema,
  projectKey: z.string(),
  number: z.number().int(),
  type: z.enum(WORK_ITEM_TYPES),
  title: z.string(),
  descriptionMd: z.string(),
  stateId: idSchema,
  priority: z.enum(PRIORITIES),
  assigneeId: idSchema.nullable(),
  reporterId: idSchema,
  parentId: idSchema.nullable(),
  dueDate: z.string().nullable(),
  position: z.string(),
  labelIds: z.array(idSchema),
  version: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
  completedAt: z.string().nullable(),
});
export type WorkItemView = z.infer<typeof workItemSchema>;

/**
 * El detalle añade los adjuntos con sus metadatos, sin urls: las firmadas se piden aparte y
 * en lote, porque caducan y no deben viajar en una respuesta que el cliente cachea (diseño §9).
 */
export const workItemDetailSchema = workItemSchema.extend({
  attachments: z.array(attachmentViewSchema),
});
export type WorkItemDetail = z.infer<typeof workItemDetailSchema>;

const titleSchema = z.string().min(1).max(500);
const descriptionSchema = z.string().max(64_000);
const dueDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'ISO date (YYYY-MM-DD)');

/** El `id` lo genera el cliente (UUID v7): reintentar la creación es idempotente por PK (diseño §7). */
export const createWorkItemSchema = z
  .object({
    id: idSchema,
    projectId: idSchema,
    type: z.enum(WORK_ITEM_TYPES).default('task'),
    title: titleSchema,
    descriptionMd: descriptionSchema.default(''),
    stateId: idSchema.optional(),
    priority: z.enum(PRIORITIES).default('none'),
    assigneeId: idSchema.nullable().optional(),
    parentId: idSchema.nullable().optional(),
    dueDate: dueDateSchema.nullable().optional(),
    labelIds: z.array(idSchema).max(50).optional(),
  })
  .strict();
export type CreateWorkItemInput = z.infer<typeof createWorkItemSchema>;
/** Lo que manda el cliente: los campos con `default` son opcionales antes de parsear. */
export type CreateWorkItemPayload = z.input<typeof createWorkItemSchema>;

/**
 * Campos limpiables como `.nullable().optional()`: `undefined` no toca, `null` limpia.
 * `projectId` y `parentId` no entran aquí: tienen operaciones propias (diseño §7).
 */
export const workItemPatchSchema = z
  .object({
    title: titleSchema.optional(),
    descriptionMd: descriptionSchema.optional(),
    type: z.enum(WORK_ITEM_TYPES).optional(),
    stateId: idSchema.optional(),
    priority: z.enum(PRIORITIES).optional(),
    assigneeId: idSchema.nullable().optional(),
    dueDate: dueDateSchema.nullable().optional(),
    labelIds: z.array(idSchema).max(50).optional(),
    expectedVersion: z.number().int().positive().optional(),
  })
  .strict();
export type WorkItemPatchInput = z.infer<typeof workItemPatchSchema>;

/**
 * Reordenar dentro de una columna. Los vecinos se validan en servidor con `FOR UPDATE`:
 * si alguno ya no está donde el cliente creía, el 409 devuelve el orden actual para rebasear
 * en vez de dejar la tarjeta donde nadie la puso (diseño §7).
 */
export const moveSchema = z
  .object({
    stateId: idSchema,
    afterId: idSchema.nullable().optional(),
    beforeId: idSchema.nullable().optional(),
  })
  .strict();
export type MoveInput = z.infer<typeof moveSchema>;

/** Orden actual de la columna que acompaña al 409 de `move`. */
export const columnOrderSchema = z.object({
  stateId: idSchema,
  items: z.array(z.object({ id: idSchema, position: z.string() })),
});
export type ColumnOrder = z.infer<typeof columnOrderSchema>;

export const BULK_LIMIT = 200;

const bulkIdsSchema = z.array(idSchema).min(1).max(BULK_LIMIT);

/**
 * El bulk sobreescribe: no acepta `expectedVersion` porque serían 200 versiones distintas.
 * A cambio sube la versión de cada item, así que un PATCH con una versión anterior recibe
 * 409 en vez de pisar el cambio en bloque (diseño §7).
 */
export const bulkPatchSchema = z
  .object({
    stateId: idSchema.optional(),
    priority: z.enum(PRIORITIES).optional(),
    assigneeId: idSchema.nullable().optional(),
    dueDate: dueDateSchema.nullable().optional(),
    type: z.enum(WORK_ITEM_TYPES).optional(),
    labels: z
      .object({
        add: z.array(idSchema).max(50).optional(),
        remove: z.array(idSchema).max(50).optional(),
      })
      .strict()
      .optional(),
  })
  .strict()
  .refine((patch) => Object.keys(patch).length > 0, 'the patch changes nothing');
export type BulkPatchInput = z.infer<typeof bulkPatchSchema>;

export const bulkSchema = z.discriminatedUnion('op', [
  z
    .object({
      op: z.literal('patch'),
      bulkId: idSchema,
      ids: bulkIdsSchema,
      patch: bulkPatchSchema,
    })
    .strict(),
  z.object({ op: z.literal('delete'), bulkId: idSchema, ids: bulkIdsSchema }).strict(),
  z
    .object({
      op: z.literal('move-project'),
      bulkId: idSchema,
      ids: bulkIdsSchema,
      projectId: idSchema,
      /** Estado de origen → estado de destino, para los que no casan por categoría. */
      stateMap: z.record(idSchema, idSchema).optional(),
    })
    .strict(),
]);
export type BulkInput = z.infer<typeof bulkSchema>;
export type BulkOp = BulkInput['op'];

/**
 * `skipped` son los ids que ya no existían al ejecutar: alguien los borró entre la selección
 * y el envío. No abortan el lote, pero el cliente necesita saberlo para podar la selección.
 */
export const bulkResultSchema = z.object({
  items: z.array(z.object({ id: idSchema, ref: z.string(), version: z.number().int() })),
  skipped: z.array(idSchema),
});
export type BulkResult = z.infer<typeof bulkResultSchema>;

export const setParentSchema = z.object({ parentId: idSchema }).strict();

export const relationSchema = z
  .object({ targetRef: z.string().min(1).max(64), kind: z.enum(RELATION_KINDS) })
  .strict();
export type RelationInput = z.infer<typeof relationSchema>;

/** `me` y `none` se resuelven en el servidor: la URL compartida sigue teniendo sentido para otro. */
export const ASSIGNEE_TOKENS = ['me', 'none'] as const;
const assigneeSchema = z.union([z.enum(ASSIGNEE_TOKENS), idSchema]);
export type AssigneeFilter = z.infer<typeof assigneeSchema>;

const csv = <T extends z.ZodTypeAny>(item: T) =>
  z.preprocess(
    (value) =>
      typeof value === 'string' ? value.split(',').filter((part) => part.length > 0) : value,
    z.array(item),
  );

export const workItemFilterSchema = z
  .object({
    project: z.string().max(8).optional(),
    type: csv(z.enum(WORK_ITEM_TYPES)).optional(),
    state: csv(idSchema).optional(),
    stateCategory: csv(stateCategorySchema).optional(),
    assignee: csv(assigneeSchema).optional(),
    label: csv(idSchema).optional(),
    priority: csv(z.enum(PRIORITIES)).optional(),
    parent: z.union([z.literal('none'), idSchema]).optional(),
    q: z.string().max(200).optional(),
    dueBefore: dueDateSchema.optional(),
    dueAfter: dueDateSchema.optional(),
    includeDone: z.coerce.boolean().optional(),
    sort: z.enum(SORTS).default('updated'),
    order: z.enum(ORDERS).default('desc'),
    cursor: z.string().max(500).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    withTotal: z.coerce.boolean().optional(),
  })
  .strict();
export type WorkItemFilter = z.infer<typeof workItemFilterSchema>;

/** El cursor lleva su propio orden: reusarlo con otro `sort` devolvería filas arbitrarias. */
export const cursorSchema = z
  .object({
    sort: z.enum(SORTS),
    order: z.enum(ORDERS),
    v: z.string(),
    id: idSchema,
  })
  .strict();
export type Cursor = z.infer<typeof cursorSchema>;

export const encodeCursor = (cursor: Cursor): string =>
  Buffer.from(JSON.stringify(cursor), 'utf8').toString('base64url');

export const decodeCursor = (raw: string): Cursor | null => {
  try {
    const parsed = cursorSchema.safeParse(
      JSON.parse(Buffer.from(raw, 'base64url').toString('utf8')),
    );
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
};

export const workItemPageSchema = z.object({
  items: z.array(workItemSchema),
  nextCursor: z.string().nullable(),
  total: z.number().int().optional(),
});
export type WorkItemPage = z.infer<typeof workItemPageSchema>;

/**
 * Una relación vista desde uno de sus extremos. `direction` no es decorativo: `blocks` y
 * `duplicates` son dirigidas, y sin ella «bloquea a X» y «bloqueado por X» son el mismo dato.
 * `relates` es simétrica y siempre se sirve como `outgoing`.
 *
 * El título y el estado del otro extremo viajan aquí porque el servidor ya carga su fila para
 * resolver la relación: sin ellos el cliente pediría un item por relación solo para pintarla.
 */
export const relationViewSchema = z
  .object({
    kind: z.enum(RELATION_KINDS),
    direction: z.enum(RELATION_DIRECTIONS),
    targetId: idSchema,
    targetRef: z.string(),
    targetTitle: z.string(),
    targetStateId: idSchema,
  })
  .strict();
export type RelationView = z.infer<typeof relationViewSchema>;
